import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { recheckEverythingOnScreen } from "@/lib/queryClient";
import { Card, InfoTip, Btn } from "@/components/common";
import { pricedSum, isPricedRow } from "@/lib/expenseTabs";
import { money, fmtDate, STATUS_META } from "@/lib/format";

// ★ ONE ANSWER TO "WHICH CURRENCY IS THIS?", FOR THIS SCREEN. (ZEN-637)
//
// A currency code is an ISO 4217 code — three ASCII letters, uppercase by
// definition — and the server compares them uppercased on BOTH sides
// (`(original_currency or "USD").upper()`). This file compared them raw in five
// places, so a row stored as "cad" was a different currency from "CAD": it would
// have been counted as its own bucket in the per-currency totals below, and read
// as a mixed selection where the server sees one currency.
//
// Nothing fails loudly when the two sides disagree — the screen simply answers a
// slightly different question and looks fine doing it. That is this change's own
// defect, one field along from money amounts, which is why it is answered ONCE
// here rather than at each site.
//
// ★ AND THE CLASS IS WIDER THAN THIS FILE. About ten other files each carry
// their own private copy of this line. Consolidating them needs a shared home
// and files this lane does not own, so it is ZEN-658 rather than smuggled in
// here. Measured first: production holds only "CAD" and "USD" and ZERO codes
// that are not already uppercase, so this closes a door rather than repairing
// anything.
const curCode = (c) => (c || "USD").toUpperCase();
import { usePaymentSources, psLabel, psOwnerName, psForPayor } from "@/lib/payments";
import { accountsPhrase } from "@/lib/accountsPhrase";
import { useParties } from "@/lib/parties";
import { toast } from "sonner";
import { Bank, CheckCircle, ClockCounterClockwise, ClockCountdown, ListBullets, Warning, ArrowRight, CaretDown, ArrowUUpLeft, ArrowCounterClockwise, PencilSimple } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { settlementFields, RECORD_TITLES } from "@/lib/recordFields";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CurrencySelect } from "@/components/CurrencySelect";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { HomeCurrencyEstimate, useHomeEstimate } from "@/components/HomeCurrencyEstimate";
import { PaidAmount } from "@/components/PaidAmount";
import { settlementState } from "@/lib/settlementState";
import { NotesField } from "@/components/NotesField";

// ★ ONE WAY TO PRINT A PRIVATE CARD'S FIGURES, used by the card and by the
// to-claim list, so the two never disagree. Each figure is in the currency it
// really is, and each currency adds up on its own: total − settled = still
// owed. A payment shows in the currency it was paid in. (ZEN-674)
const joinCur = (rows, field) => rows.map((r) => money(r[field], r.currency)).join(" + ");
// The card front is the open settlement, so its wording goes by the
// currencies still awaiting — not by every currency the card has ever held.
// A card owed only Canadian dollars reads "CAD … or USD …" even when an
// expense already paid back was in US dollars. (ZEN-755)
const openRows = (c) => (c.by_currency || []).filter((r) => r.owed > 0.005);
const cardKind = (c) => {
  const open = openRows(c);
  if (c.has_older_repayment || open.length === 0) return "usd";
  if (open.length > 1) return "mixed";
  return curCode(open[0].currency) === "USD" ? "usd" : "one";
};

/** What is awaiting settlement, in every currency still awaiting. */
function owedText(c) {
  const kind = cardKind(c);
  const open = openRows(c);
  if (kind === "one") return `${money(open[0].owed, open[0].currency)} or ${money(c.net, "USD")}`;
  if (kind === "mixed") return joinCur(open, "owed");
  return money(c.net, "USD");
}

/** "Awaiting settlement · 3 expenses" — the open settlement, and how many it holds. */
function awaitingLabel(c) {
  const n = c.awaiting_count;
  // The status's own name, from the one shared list — never typed here.
  const label = `${STATUS_META.confirmed.label} settlement`;
  if (c.has_older_repayment || !n) return label;
  return `${label} · ${n} expense${n === 1 ? "" : "s"}`;
}

// The card front shows only the open settlement; each finished one is a line
// under History. (ZEN-675) A card with an older lump-sum repayment cannot be
// split that way, so it keeps its running figures.
function CardBreakdown({ c, className = "" }) {
  const kind = cardKind(c);
  const est = useHomeEstimate(kind === "mixed" && c.net > 0.005 ? c.net : null);
  if (c.has_older_repayment) {
    return (
      <div className={className}>
        <div>Already settled: {money(c.settled, "USD")}</div>
        <div>All expenses: {money(c.gross, "USD")}</div>
        <div data-testid="card-older-repayment-note">US dollars only: an older repayment here didn&apos;t say which expenses it paid back.</div>
      </div>
    );
  }
  if (kind !== "mixed" || !(c.net > 0.005)) return null;
  return (
    <div className={className}>
      <div>Total: {money(c.net, "USD")}</div>
      <HomeCurrencyEstimate estimate={est.data?.estimate} />
    </div>
  );
}

/** One list for the Method box wherever a repayment is recorded or edited:
 *  the family's own accounts and cards. `keep` is a value already on the
 *  record, so opening the edit box never loses it. (ZEN-675) */
// 529 settlements are not part of this ruling and keep the lists they had.
const METHODS_529_EDIT = ["Cash", "E-transfer", "Bank transfer", "Check", "Wire", "Other"];

function useMethodOptions(keep = []) {
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(false);
  const psLabels = (psData?.sources || []).map((s) => psLabel(s, psOwnerName(s, students, parties)));
  return [...new Set([...psLabels, ...keep.filter(Boolean)])];
}

/** The accounts a private settlement moves between (ZEN-799, Maryse 24 Oct
 *  2026): From = the person paying back, To = the person who paid, each with
 *  the accounts the expense form offers that person under Paid by. Stored as
 *  the account's name, as settlements always have; `keep` holds names a
 *  record already carries, so editing never loses them. */
function useSettlementAccounts(sid, payorRef, reimburserRef, keep = {}) {
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(true);
  const all = psData?.sources || [];
  const asPayor = (ref) => (ref === "owner" || ref === "student" ? ref : "party");
  const names = (ref, kept) => [...new Set([
    ...psForPayor(all, asPayor(ref), sid, null).map((x) => psLabel(x, psOwnerName(x, students, parties))),
    ...(kept ? [kept] : [])])];
  return { fromOpts: names(reimburserRef, keep.from), toOpts: names(payorRef, keep.to) };
}

/** From account and To account, side by side: the Settle panel and Edit. */
function SettlementAccountBoxes({ fromOpts, toOpts, from, to, onFrom, onTo, testid, labelClass, selectClass }) {
  const box = (label, value, onChange, opts, id) => (
    <label className={labelClass}>{label}
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-${id}`} className={selectClass}>
        <option value="">— None —</option>
        {opts.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </label>
  );
  return <>{box("From account", from, onFrom, fromOpts, "from")}{box("To account", to, onTo, toOpts, "to")}</>;
}

function MoneyArea({ title, subtitle, badge, badgeColor, cards, testid, onUndo, studentId, area = "private", taxYear }) {
  const qc = useQueryClient();
  const active = (cards || []).filter((c) => Math.abs(c.net) > 0.005 || Math.abs(c.gross) > 0.005);
  const [histKey, setHistKey] = useState(null);
  const [awaitKey, setAwaitKey] = useState(null);
  const [expKey, setExpKey] = useState(null);
  // OPENING A LIST THAT EXPLAINS THE CARD RE-CHECKS THE CARD. (ZEN-735)
  // The lists below are fetched fresh when they open; the figure above them
  // was not, so on 6–7 October the card said $50.77 over a list adding up to
  // $71.93. Now both are the same moment. Closing re-checks nothing.
  const opening = (isOpen) => { if (!isOpen) recheckEverythingOnScreen(qc); };
  return (
    <Card data-testid={testid}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-[#1f2937]">{title}</h3>
        {badge && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: badgeColor }}>{badge}</span>}
      </div>
      {/* The Overview's section headings now say which part of the page a box
          is in, so it passes no badge or subtitle (ZEN-814 Q7). */}
      {subtitle ? <p className="mb-3 text-xs text-[#6b7280]">{subtitle}</p> : <div className="mb-2" />}
      {active.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#C2C9D1] px-3 py-4 text-center text-xs text-[#6b7280]">No active relationships.</div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {active.map((c) => {
            // WHICH CARD, NOT WHERE IT SITS. An open list is remembered by the two
            // people the card is between. By position, a refresh that reorders
            // the cards (one settled elsewhere drops out) would leave the open
            // list — and its Mark reimbursed button — under somebody else's card.
            // Opening a list now refreshes the cards, so this matters. (ZEN-735)
            const i = cardKey(c);
            const state = settlementState(c);
            const settled = state === "settled";
            const overSettled = state === "over";
            return (
              <div key={i} className="rounded-lg border border-[#C2C9D1] p-3" data-testid={`settle-card-${c.reimburser_nick}-${c.payor_nick}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold">{c.reimburser_nick} → {c.payor_nick}</span>
                  {settled && <span className="rounded-full bg-[#077A39] px-2 py-0.5 text-[10px] font-bold text-white">Settled</span>}
                  {overSettled && <span className="rounded-full bg-[#D97706] px-2 py-0.5 text-[10px] font-bold text-white">Over-settled</span>}
                </div>
                {area === "private" && !settled && !overSettled && <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">{awaitingLabel(c)}</div>}
                <div className="mt-1 text-xl font-extrabold tnum" style={{ color: settled ? "#077A39" : overSettled ? "#D97706" : "#F01919" }}>
                  {settled ? "Settled" : overSettled ? `${money(Math.abs(c.net), "USD")} credit` : owedText(c)}
                </div>
                {area === "private"
                  ? <CardBreakdown c={c} className="mt-0.5 text-[11px] text-[#6b7280]" />
                  : <div className="mt-0.5 text-[11px] text-[#6b7280]">total {money(c.gross, "USD")} · settled {money(c.settled, "USD")}</div>}
                {overSettled && (
                  <div className="mt-1 text-[11px] text-[#92600A]">Credit carries forward to next settlement between these parties.</div>
                )}
                {/* A private card's payments need not share accounts, so the method is
                    on each payment under History, not on the card front. (ZEN-755) */}
                {area !== "private" && (() => { const pm = [...new Set(c.reimbursement_methods || [])].join(", ") || (c.funded_via || []).join(", "); return pm ? <div className="mt-0.5 text-[11px] text-[#9aa1a9]">Payment method: {pm}</div> : null; })()}
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  {/* View expenses — always available */}
                  <button onClick={() => { opening(expKey === i); setExpKey(expKey === i ? null : i); setAwaitKey(null); }} data-testid={`settle-exp-btn-${c.reimburser_nick}-${c.payor_nick}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold ${settled ? "text-[#034AA2]" : "text-[#D97706]"} hover:underline`}>
                    <ListBullets size={12} weight="bold" /> {expKey === i ? "Hide expenses" : "View expenses"}
                  </button>
                  {/* 529 area — link to Withdrawals page (always show for 529 when outstanding) */}
                  {area === "529" && state === "owed" && (
                    <a href="/withdrawals" data-testid={`settle-529-link-${c.reimburser_nick}-${c.payor_nick}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#034AA2] hover:underline">
                      <Bank size={12} weight="bold" /> Add 529 distribution
                    </a>
                  )}
                  {/* Settle — only when outstanding */}
                  {state === "owed" && (
                    <button onClick={() => { opening(awaitKey === i); setAwaitKey(awaitKey === i ? null : i); setExpKey(null); }} data-testid={`settle-awaiting-btn-${c.reimburser_nick}-${c.payor_nick}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#077A39] hover:underline">
                      <CheckCircle size={12} weight="bold" /> Settle
                    </button>
                  )}
                  <button onClick={() => { opening(histKey === i); setHistKey(histKey === i ? null : i); }} data-testid={`settle-history-btn-${c.reimburser_nick}-${c.payor_nick}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#034AA2] hover:underline">
                    <ClockCounterClockwise size={12} weight="bold" /> History
                  </button>
                </div>
                {expKey === i && <SettlementExpensesList sid={studentId} c={c} area={area} taxYear={taxYear} settled={settled} />}
                {awaitKey === i && <SettlementAwaiting sid={studentId} c={c} area={area} taxYear={taxYear} />}
                {histKey === i && <SettlementHistory sid={studentId} c={c} area={area} />}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** Which card this is: the beneficiary and the two people it is between. */
function cardKey(c) {
  return `${c.student_id || ""}|${c.payor_ref}|${c.reimburser_ref}`;
}


// After every save in this file, the figures are re-checked by the one rule in
// lib/api.js, not by a list of screens here that would have to be kept right
// by hand. (ZEN-735) The first argument stays only because Dashboard.jsx passes
// it; nothing here uses it any more.
async function doUndoLastSettlement(_qc, sid, c, area = "private") {
  if (!sid) { toast.error("Open a single beneficiary to undo this"); return; }
  try {
    await api.post("/settlements/undo-last", { student_id: sid, area, payor_ref: c.payor_ref, reimburser_ref: c.reimburser_ref });
    toast.success("Settlement undone — linked expenses reverted to Awaiting.");
  } catch (e) { toast.error(e?.response?.status === 404 ? "Nothing to undo" : "Could not undo"); }
}

// ── ZEN-314: one status list, not four ───────────────────────────────────────
//
// This badge used to carry its own copy of "which statuses exist, what each one
// is called, and what colour it is" — two hand-written objects sitting a few
// lines above the markup. When a sixth status ("scheduled" — an expense recorded but
// not yet paid) was added to the app, those two copies were not updated, and
// nothing said so: the markup fell back to `label[status] || status`, so the row
// rendered the raw database word on a plain grey chip, beside five siblings with
// proper names and colours. A silent fallback is what let a missing entry look
// like a working screen.
//
// So the list is no longer restated here. STATUS_META in lib/format.js is the one
// place that answers what a status is called and what colour it wears, and every
// other display in the app already reads it (StatusChip, the Overview key, the
// Expenses table). A seventh status arrives here for free.
//
// The chip KEEPS its own small dimensions rather than reusing StatusChip: this is
// a dense drill-down list that has to survive a 375px phone, and the full-size
// chip would push the amount column off the row. The list of statuses is shared;
// only the size is local.
//
// The fallback below still exists, because a component should not crash on an
// unknown value — but it now HUMANISES rather than leaking storage: an unknown
// "some_new_status" renders as "Some new status", never as the raw word. The real
// guard against that path is backend/tests/test_status_display_coverage.py, which
// fails the build if a status exists that this screen or the Expenses legend
// cannot name.
const humanise = (status) => {
  const s = String(status || "").replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Unknown";
};

export function SettlementStatusBadge({ status }) {
  const meta = STATUS_META[status];
  return (
    <span
      data-testid={`settle-exp-status-${status}`}
      className="mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
      style={{ background: meta?.bg || "#6b7280", color: meta?.fg || "#fff" }}>
      {meta?.short || humanise(status)}
    </span>
  );
}

function SettlementExpensesList({ sid, c, area = "private", taxYear, settled = false }) {
  const { data, isLoading, error } = useQuery({
    // A private card lists what is still awaiting; what has been paid back is
    // under History, with the payment that covered it. (ZEN-755) Only this
    // card's arrangement is listed, as the Settle panel does. (ZEN-659)
    queryKey: ["settlement-expenses", sid, c.payor_ref, c.reimburser_ref, area, taxYear],
    queryFn: async () => (await api.get(`/settlements/expenses?student_id=${sid}&payor_ref=${encodeURIComponent(c.payor_ref)}&reimburser_ref=${encodeURIComponent(c.reimburser_ref)}&area=${area}${area === "private" ? "&awaiting_only=true" : ""}${taxYear ? `&tax_year=${taxYear}` : ""}`)).data,
    enabled: !!sid,
  });
  const [fixing, setFixing] = useState(null);
  const items = data?.items || [];
  const hasForeign = items.some((e) => curCode(e.original_currency) !== "USD");
  // ★ ONE ROW SET FOR BOTH FIGURES ON THAT LINE, OR THEY LIE ABOUT EACH OTHER.
  //
  // The dollar figure beside this comes from the server, which now LEAVES OUT an
  // expense it cannot price. This summed `original_amount || 0` over EVERY row,
  // so the two totals sat side by side on one line describing different sets of
  // rows — and the foreign one silently counted a row the dollar one had
  // dropped. That is the ZEN-44 defect and it is step four of this ticket's own
  // brief; I fixed it in the settle panel below and missed it here.
  // (Adversarial review, round 11.)
  //
  // COUNTS EXACTLY WHAT THE SERVER COUNTED — the rows it could price — so the
  // two figures always describe the same expenses.
  //
  // AND A CURRENCY WHOSE TOTAL CANNOT BE STATED IS NOT STATED. If a counted row
  // has no real number in its own currency, that currency is dropped rather than
  // shown short: a blank says "we cannot tell you this", a number says something
  // untrue. Same choice the shared money rule makes when a total has no honest
  // answer.
  const countedItems = items.filter(isPricedRow);
  const fxTotals = (() => {
    if (!hasForeign) return {};
    const acc = {};
    const cannotState = new Set();
    for (const e of countedItems) {
      const cur = curCode(e.original_currency);
      if (cur === "USD") continue;
      // ★ THE SERVER'S VERDICT, NOT A SECOND OPINION. (ZEN-637)
      //
      // My first version tested "is it a real number" here — re-deriving a rule
      // the server had already answered and stamped on the row. It agreed on the
      // ordinary case and disagreed on exactly the ones that matter: a negative
      // amount and one above the app's ceiling are real numbers, and the server
      // refuses both. So this total would have shown a figure built from money
      // the server would not record.
      //
      // That is the boundary this file states twice already — the browser is
      // TOLD, it does not work it out — and I broke my own rule twenty minutes
      // after writing it. The number check stays as a GUARANTEE against a row
      // from an old cache that carries no verdict at all.
      // (Adversarial review, round 12.)
      const amt = e.original_amount;
      if (e.is_native_unpriced === true
          || typeof amt !== "number" || !Number.isFinite(amt)) { cannotState.add(cur); continue; }
      acc[cur] = (acc[cur] || 0) + amt;
    }
    for (const cur of cannotState) delete acc[cur];
    return acc;
  })();

  const markReconciled = async (expId) => {
    setFixing(expId);
    try {
      await api.post("/settlements/reconcile-expenses", {
        student_id: sid, payor_ref: c.payor_ref, reimburser_ref: c.reimburser_ref,
        expense_ids: [expId], area,
      });
      toast.success("Marked as reconciled");
    } catch (e) { toast.error(apiErr(e)); } finally { setFixing(null); }
  };

  return (
    <div className="mt-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2.5" data-testid="settlement-expenses-list">
      <div className={`mb-1.5 text-[11px] font-bold uppercase tracking-wide ${settled ? "text-[#6b7280]" : "text-[#D97706]"}`}>
        Expenses · {c.reimburser_nick} → {c.payor_nick}
      </div>
      {isLoading ? <div className="text-xs text-[#6b7280]">Loading…</div>
        : error ? <div className="text-xs text-[#B91C1C]" data-testid="settlement-list-refused">{apiErr(error)}</div>
        : items.length === 0 ? <div className="text-xs text-[#6b7280]">{area === "private" ? "No expenses are waiting to be settled. Finished settlements are under History." : "No expenses found for this settlement."}</div>
          : <>
            {hasForeign && (
              <div className="flex items-center gap-2 pb-1 text-[9px] font-bold uppercase tracking-wide text-[#9aa1a9]">
                <span className="flex-1" />
                <span className="w-20 text-right">FX</span>
                <span className="w-20 text-right">USD</span>
              </div>
            )}
            <div className="divide-y divide-[#E9E9EB]">
              {items.map((e) => (
                <div key={e.id} className="flex items-start gap-2 py-1.5 text-xs" data-testid={`settle-exp-row-${e.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{e.payee || "—"}</div>
                    <div className="text-[#6b7280]">{fmtDate(e.transaction_date)} · {e.category}</div>
                    {e.description && <div className="text-[10px] text-[#9aa1a9] truncate">{e.description}</div>}
                  </div>
                  {/* The one row that is a BUTTON, not a badge — clicking it marks the
                      expense reconciled. It still has to LOOK like its status: it
                      carried its own red (#F01919) and its own label, so the single
                      Awaiting row on this list was a different red from every other
                      Awaiting badge in the app. Same duplicated-list defect as the
                      two maps above, one control further on. The hover green is
                      local and stays local — that is the button's affordance, not
                      the status's colour. (Adversarial pre-merge review, round 4.) */}
                  {e.status === "confirmed" ? (
                    <button onClick={() => markReconciled(e.id)} disabled={fixing === e.id}
                      data-testid={`settle-exp-fix-${e.id}`}
                      className="mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold transition-colors hover:!bg-[#077A39] disabled:opacity-50"
                      style={{ background: STATUS_META.confirmed.bg, color: STATUS_META.confirmed.fg }}
                      title="Click to mark as reconciled">
                      {fixing === e.id ? "…" : `${STATUS_META.confirmed.short} ✓`}
                    </button>
                  ) : (
                    <SettlementStatusBadge status={e.status} />
                  )}
                  {hasForeign && <div className="w-20 shrink-0 whitespace-nowrap text-right tnum text-[#6b7280]">{curCode(e.original_currency) !== "USD" ? money(e.original_amount, curCode(e.original_currency)) : ""}</div>}
                  <div className="w-20 shrink-0 whitespace-nowrap text-right tnum font-bold">{money(e.usd_amount, "USD")}</div>
                </div>
              ))}
            </div>
            <div className="mt-1 flex items-center gap-2 border-t border-[#C2C9D1] pt-1.5 text-[11px] font-bold">
              <span className="flex-1 text-[#6b7280]">{items.length} expense{items.length === 1 ? "" : "s"}</span>
              {hasForeign && (
                <div className="w-20 shrink-0 text-right tnum text-[#6b7280]">
                  {Object.entries(fxTotals).map(([cur, amt]) => (
                    <div key={cur}>{money(amt, cur)}</div>
                  ))}
                </div>
              )}
              <span className={`w-20 shrink-0 text-right tnum ${settled ? "" : "text-[#D97706]"}`}>{money(data?.total || 0, "USD")}</span>
            </div>
          </>}
    </div>
  );
}

function SettlementAwaiting({ sid, c, area = "private", taxYear }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["settlement-awaiting", sid, c.payor_ref, c.reimburser_ref, area, taxYear],
    queryFn: async () => (await api.get(`/settlements/awaiting?student_id=${sid}&payor_ref=${encodeURIComponent(c.payor_ref)}&reimburser_ref=${encodeURIComponent(c.reimburser_ref)}&area=${area}${taxYear ? `&tax_year=${taxYear}` : ""}`)).data,
    enabled: !!sid,
  });
  const accountOpts = useMethodOptions();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, enabled: area !== "private" });
  const group = data?.groups?.[0];
  const items = group?.items || [];
  const [sel, setSel] = useState(null);
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("");
  const [fromAcct, setFromAcct] = useState("");
  const [toAcct, setToAcct] = useState("");
  const { fromOpts, toOpts } = useSettlementAccounts(sid, c.payor_ref, c.reimburser_ref);
  const [note, setNote] = useState("");
  const [payCur, setPayCur] = useState("USD");
  const [busy, setBusy] = useState(false);
  // ★ A ROW THE APP CANNOT PRICE IS NEVER OFFERED FOR SELECTION. (ZEN-637)
  //
  // This panel pre-ticks everything, and used to pre-tick these too — so the
  // app SELECTED an expense on the family's behalf and then refused to record
  // it. Offering something you will reject is the same broken promise as a
  // figure that is quietly short; it just fails louder.
  //
  // They are still SHOWN, deliberately, and that is the harder half of the
  // decision. Hiding them would make this panel answer "what does this person
  // still owe me?" with a quietly short list — a family would settle up and
  // believe they were square while one expense went unaccounted for. That is
  // the defect this whole change exists to remove, moved from a total into a
  // list. Better a row you cannot tick than an answer that is silently
  // incomplete. (Owner's decision, 2026-09-16, having asked exactly this.)
  //
  // ASKED OF THE DOLLAR FIGURE ONLY, not of the own-currency one. Whether the
  // native amount matters depends on the currency the family picks, which
  // depends on what is selected — so making the default selection depend on it
  // would be circular. A row with no dollar figure can never be settled in any
  // currency, which is a fact about the row alone.
  const canBeSettled = (e) => isPricedRow(e);
  const settleableIds = items.filter(canBeSettled).map((e) => e.id);
  const selected = sel === null ? settleableIds : sel;
  const toggle = (id) => {
    setSel((p) => {
      const cur = p === null ? settleableIds : p;
      // ★ TAKING A ROW OUT IS ALWAYS ALLOWED. ONLY PUTTING ONE IN IS GUARDED.
      //
      // My first version refused BOTH directions, which built a dead end: a row
      // already in the selection when its dollar figure goes away (an ordinary
      // refetch after somebody clears a rate) stays selected, blocks the button
      // — and the note on screen tells the family to unselect it while the box
      // is disabled and this function returns immediately. The only way out was
      // reloading the page. A guard that traps somebody is worse than the thing
      // it guards against. (Adversarial review, round 8.)
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      // The guard is here as well as on the box: a disabled control is a
      // courtesy, and this is the one that cannot be clicked past.
      const row = items.find((e) => e.id === id);
      if (row && !canBeSettled(row)) return cur;
      return [...cur, id];
    });
  };
  const selItems = items.filter((e) => selected.includes(e.id));
  // ★ ONE LIST, BOTH FIGURES, AND THE COUNT — OR THEY LIE ABOUT EACH OTHER.
  //
  // The dollar total and the native-currency total are rendered side by side,
  // and the native one also goes inside the "Mark reimbursed" button. If a row
  // left one half and stayed in the other, two figures about the SAME selection
  // would sit next to each other describing different selections. That is the
  // ZEN-44 defect, and ZEN-632 caught me introducing it here once already.
  //
  // So the settleable rows are worked out ONCE, and everything below — both
  // figures, the count in the note, and the guard on the button — is derived
  // from that one list. They cannot disagree because there is nothing to
  // disagree with.
  //
  // TWO QUESTIONS, AND ONLY ONE OF THEM IS THIS SCREEN'S TO ASK:
  //
  //   isPricedRow      — the SERVER's verdict on the US-dollar figure, carried
  //                      on the row. The rule, and it knows the negative and
  //                      ceiling bounds the browser cannot see.
  //   a real native
  //   number           — a GUARANTEE, not a rule: whatever else is true, a
  //                      figure a family reads is never built out of something
  //                      that is not a number. `|| 0` used to answer this
  //                      locally, dropping a missing native amount to zero in
  //                      one half while the other half stayed whole.
  //
  // The bounds the server also applies to the native figure — negative, and
  // above the amount the app can record — are deliberately NOT repeated here.
  // Repeating them would look like rigour without being any: the reconcile
  // route refuses such a row outright, and that refusal is the control. This is
  // the early warning. (ZEN-637; the boundary is ZEN-632's, rounds 8 and 9.)
  //
  // ★ AND IT ASKS THE NATIVE QUESTION OF EXACTLY THE ROWS THE SERVER ASKS IT OF.
  //
  // The server only needs a row's own-currency amount when that row is IN the
  // currency being paid in — a row in any other currency contributes nothing to
  // the native figure, it only forces the whole settlement to US dollars. So
  // requiring a real native amount from every row would disable this button for
  // a row the server would happily settle, which is blocking honest work: the
  // very thing the refusal on the server is written to avoid. The condition is
  // mirrored rather than approximated.
  // Which single currency this selection is in, if any — declared HERE, above
  // the rows it helps filter, because the settleable list now depends on it.
  // ★ UPPERCASED, BECAUSE THE SERVER UPPERCASES AND THESE TWO MUST AGREE.
  //
  // The reconcile route compares `(original_currency or "USD").upper()` against
  // an uppercased payment currency. This did neither, so a legacy row stored as
  // "cad" counted as a DIFFERENT currency from "CAD" — the browser would see two
  // currencies where the server sees one, decide the selection is mixed, and
  // show no own-currency figure for a settlement the server would happily record
  // in that currency. One rule, spelled the same way on both sides.
  // (Adversarial review, round 9.)
  const selCurs = [...new Set(selItems.map((e) => curCode(e.original_currency)))];
  const nativeCur = selCurs.length === 1 && selCurs[0] !== "USD" ? selCurs[0] : null;
  // WHEN the own-currency figure matters at all: only if this reimbursement is
  // going to be RECORDED in that currency. Spelled the same way the server
  // spells it, and the same way the button's own label decides whether to show
  // a native figure, so the three cannot drift apart.
  // ★ THE CHOSEN CURRENCY MUST ACTUALLY BE THE ROWS' CURRENCY, not merely
  // "not US dollars".
  //
  // The effect below resets the payment currency when the selection's currency
  // changes — but an effect runs AFTER the render that caused it. So for one
  // render the family could have CAD chosen while the rows are now EUR, and
  // this read true, applying the own-currency test against a currency nothing
  // is denominated in. Comparing the two directly removes the window instead of
  // relying on a correction that arrives a moment later.
  // (Adversarial review, round 9.)
  const payCurNorm = curCode(payCur);
  const recordsInNativeCurrency = !!nativeCur && payCurNorm === nativeCur;
  // ★ THE ONE ANSWER: WHICH CURRENCY THIS SETTLEMENT IS ACTUALLY RECORDED IN.
  //
  // Three rounds of adversarial review each found a different fault in these
  // few lines, and every one had the same cause: "are we recording in the rows'
  // own currency?" was answered SEPARATELY by the row filter, the button's
  // label, the currency that gets submitted, and the effect that resets the
  // choice — four answers, drifting apart. That is precisely the defect this
  // whole change exists to remove, three fields along from where it started.
  //
  // So it is answered ONCE here, spelled the way the server spells it, and the
  // submission, the label and the filter all read this. They cannot describe
  // different settlements because there is only one thing to describe.
  //
  // IT ALSO CLOSES THE WINDOW RATHER THAN GUARDING IT. The reset effect runs
  // AFTER the render that triggers it, so for one render the family could hold
  // a currency the rows no longer use. The review's suggestion was to disable
  // the button during that window; sending the currency the app has DECIDED on
  // means there is no stale value to submit and nothing to disable.
  // (Adversarial review, rounds 8, 9 and 10.)
  const recordingCurrency = recordsInNativeCurrency ? nativeCur : "USD";
  // WHETHER it is usable: the SERVER's verdict, carried on the row, never
  // re-derived. It knows the negative and ceiling bounds the browser cannot
  // see; a browser-side "is it a number" agreed with it on the ordinary case
  // and disagreed on exactly the rows that matter, so the screen offered a row
  // this route would reject. (Adversarial review, round 2.)
  const selPriced = selItems.filter(
    (e) => isPricedRow(e) && (!recordsInNativeCurrency || e?.is_native_unpriced !== true));
  const selUnpriced = selItems.length - selPriced.length;
  // ★ A ROW NOBODY CAN TICK NEEDS A REASON GIVEN, NOT JUST A GREYED BOX.
  //
  // These never reach selUnpriced above, precisely BECAUSE they can no longer
  // be selected — so the note that explains an excluded row would have gone
  // silent for the commonest case at the moment it started mattering most.
  // Counted from the panel rather than from the selection. (ZEN-637)
  const blockedInPanel = items.length - settleableIds.length;
  const selTotal = pricedSum(selPriced).total;
  // 529 settlements only; a private one asks From account and To account. (ZEN-799)
  const methodOpts = [...new Set([...accountOpts, ...(settings?.payment_methods || []), ...items.map((e) => e.funded_via).filter(Boolean)])];
  const commonFunded = (() => { const s = [...new Set(selItems.map((e) => e.funded_via).filter(Boolean))]; return s.length === 1 ? s[0] : ""; })();
  const hasForeign = items.some((e) => curCode(e.original_currency) !== "USD");
  // ★ NO FIGURE AT ALL RATHER THAN A WRONG ONE. (ZEN-637)
  //
  // When the reimbursement is being recorded in the family's own currency, every
  // settleable row already carries a usable amount in it — refused otherwise,
  // above and on the server. But this figure is also DISPLAYED when the family
  // is paying in US dollars, and there such a row is perfectly settleable while
  // its own-currency amount is not something that can be added up.
  //
  // `|| 0` used to answer that by dropping the row silently, so the two figures
  // side by side described different selections. The honest answer is to state
  // no own-currency total at all: a blank says "we cannot tell you this", where
  // a number says something untrue. That is the same choice money_totals makes
  // when a total has no honest answer. The dollar figure beside it is unaffected
  // and still correct.
  const nativeFigureCanBeStated = selPriced.every((e) => e?.is_native_unpriced !== true);
  const selNativeTotal = nativeCur && nativeFigureCanBeStated
    ? selPriced.reduce((s, e) => s + e.original_amount, 0) : null;
  // Normalised, for the same reason as everything else here: a raw comparison
  // would leave the choice standing when only its case differed.
  React.useEffect(() => { if (payCurNorm !== "USD" && payCurNorm !== nativeCur) setPayCur("USD"); }, [nativeCur]); // eslint-disable-line

  const reconcile = async () => {
    // ★ AN EARLY WARNING NOW, RATHER THAN A FALSE PROMISE. (ZEN-637)
    //
    // This guard was written for ZEN-632 and then REMOVED from it, because an
    // adversarial review showed it was enforcing a rule the application did not
    // have: the server had no refusal on the reconcile path at all, so anything
    // that is not this browser walked straight past it. A guard in the browser
    // is a courtesy, never a control.
    //
    // It is back because the control now exists. `POST /settlements/
    // reconcile-expenses` refuses a selection containing an expense it cannot
    // price, and that refusal is the rule. This check does not enforce it — it
    // saves the family a round trip and explains the problem where they can see
    // it, next to the figure and the rows.
    //
    // So it must never be the ONLY thing standing between an unpriced expense
    // and a recorded reimbursement, and it no longer is.
    // (Rounds 8 and 9 of #380; owner ruling to split, 2026-09-15.)
    if (selUnpriced > 0) {
      toast.error("Some selected expenses have no usable amount on record. "
                  + "Open them and check the exchange rate, or unselect them.");
      return;
    }
    if (!selected.length) { toast.error("Select at least one expense"); return; }
    if (!date) { toast.error("Choose a date paid"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/settlements/reconcile-expenses", {
        student_id: sid, payor_ref: c.payor_ref, reimburser_ref: c.reimburser_ref,
        // The currency DECIDED above, never the raw control value — see the note
        // on recordingCurrency. A stale choice cannot be submitted.
        expense_ids: selected, date, notes: note, currency: recordingCurrency, area,
        ...(area === "private" ? { from_account: fromAcct, to_account: toAcct } : { method }),
      });
      const st = r.settlement || {};
      const amtStr = (st.currency && st.currency !== "USD") ? money(st.amount, st.currency) : money(st.amount_usd ?? r.amount, "USD");
      toast.success(`Reimbursed ${r.reconciled} expense${r.reconciled === 1 ? "" : "s"} · ${amtStr}`);
      setSel(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!isLoading && !error && items.length === 0) return null;
  return (
    <div className="mt-2 rounded-md border border-[#D97706]/30 bg-[#FCF5EE] p-2.5" data-testid="settlement-awaiting">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#D97706]">Awaiting settlement · {c.reimburser_nick} → {c.payor_nick}</div>
      {isLoading ? <div className="text-xs text-[#6b7280]">Loading…</div>
        : error ? <div className="text-xs text-[#B91C1C]" data-testid="settlement-list-refused">{apiErr(error)}</div>
        : items.length === 0 ? <div className="text-xs text-[#6b7280]">No expenses awaiting settlement.</div>
          : <>
            {hasForeign && (
              <div className="flex items-center gap-2 pb-1 text-[9px] font-bold uppercase tracking-wide text-[#9aa1a9]" data-testid="settlement-awaiting-headers">
                <span className="flex-1" />
                <span className="w-24 text-right">{[...new Set(items.map((e) => curCode(e.original_currency)).filter((c2) => c2 !== "USD"))][0] || "FX"}</span>
                <span className="w-20 text-right">USD</span>
              </div>
            )}
            <div className="divide-y divide-[#E9E9EB]">
              {items.map((e) => (
                <label key={e.id} className={`flex items-center gap-2 py-1.5 text-xs ${canBeSettled(e) ? "cursor-pointer" : "cursor-not-allowed"}`} data-testid={`settlement-awaiting-row-${e.id}`}>
                  <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggle(e.id)}
                    /* Disabled only when it is NOT already selected — otherwise a row
                       that lost its figure while ticked could never be un-ticked. */
                    disabled={!canBeSettled(e) && !selected.includes(e.id)}
                    title={canBeSettled(e) ? undefined : "This expense has no usable US-dollar amount on record, so it cannot be reimbursed yet. Open it and check the exchange rate."}
                    data-testid={`settlement-awaiting-check-${e.id}`} className="h-3.5 w-3.5 shrink-0 accent-[#D97706] disabled:cursor-not-allowed disabled:opacity-40" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{e.payee}</div>
                    <div className="text-[#6b7280]">{fmtDate(e.transaction_date)} · {e.category}</div>
                  </div>
                  {hasForeign && <div className="w-24 whitespace-nowrap text-right tnum text-[#6b7280]">{curCode(e.original_currency) === "USD" ? "" : money(e.original_amount, curCode(e.original_currency))}</div>}
                  <div className="w-20 whitespace-nowrap text-right tnum font-bold">{money(e.usd_amount, "USD")}</div>
                </label>
              ))}
            </div>
            <div className="mt-1.5 flex items-center gap-2 border-t border-[#D97706]/20 pt-1.5 text-xs font-bold" data-testid="settlement-awaiting-total">
              <span className="flex-1">{selected.length} selected</span>
              {hasForeign && <span className="w-24 text-right tnum text-[#6b7280]">{nativeCur && selNativeTotal !== null ? money(selNativeTotal, nativeCur) : ""}</span>}
              <span className="w-20 text-right tnum text-[#D97706]">{money(selTotal, "USD")}</span>
            </div>
            {/* "no usable amount", not "no US-dollar amount". Almost always the
                cause is a missing exchange rate, which is why that is the advice
                — but a row is also left out when its own currency amount is not
                a real number, and telling that family their dollar figure is
                missing would send them looking for the wrong thing. (ZEN-637) */}
            {blockedInPanel > 0 && (
              <div className="mt-1 text-[11px] font-semibold text-[#B45309]" data-testid="settlement-blocked-note">
                {blockedInPanel} expense{blockedInPanel !== 1 ? "s" : ""} below
                {blockedInPanel !== 1 ? " have" : " has"} no usable US-dollar amount on record, so
                {blockedInPanel !== 1 ? " they cannot" : " it cannot"} be reimbursed yet and
                {blockedInPanel !== 1 ? " are" : " is"} not in these figures. Open
                {blockedInPanel !== 1 ? " them" : " it"} and check the exchange rate.
              </div>
            )}
            {selUnpriced > 0 && (
              <div className="mt-1 text-[11px] font-semibold text-[#B45309]" data-testid="settlement-unpriced-note">
                {selUnpriced} selected expense{selUnpriced !== 1 ? "s have" : " has"} no
                usable amount on record, so {selUnpriced !== 1 ? "they are" : "it is"} not
                in these figures — and this cannot be marked reimbursed until
                {selUnpriced !== 1 ? " they are" : " it is"} fixed. Open
                {selUnpriced !== 1 ? " them" : " it"} and check the exchange rate, or unselect
                {selUnpriced !== 1 ? " them" : " it"}.
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-[#D97706]/20 pt-2">
              <label className="text-[10px] font-semibold text-[#6b7280]">Date paid
                <div className="mt-0.5"><UnifiedDateInput value={date} onChange={(v) => setDate(v)} testid="settlement-reconcile-date" hideHint /></div>
              </label>
              {nativeCur && (
                <label className="text-[10px] font-semibold text-[#6b7280]">Paid in
                  {/* The same two choices as before, in the one picker. (ZEN-830) */}
                  <div className="mt-0.5 w-48"><CurrencySelect value={payCur} onChange={setPayCur} restrictTo={["USD", nativeCur]} flat testid="settlement-reconcile-currency" className="!h-8 text-xs font-semibold" /></div>
                </label>
              )}
              {area === "private" ? (
                <SettlementAccountBoxes fromOpts={fromOpts} toOpts={toOpts} from={fromAcct} to={toAcct} onFrom={setFromAcct} onTo={setToAcct}
                  testid="settlement-reconcile" labelClass="text-[10px] font-semibold text-[#6b7280]"
                  selectClass="mt-0.5 block h-8 w-48 rounded border border-[#C2C9D1] px-1.5 text-xs" />
              ) : (
                <label className="text-[10px] font-semibold text-[#6b7280]">
                  Method (optional)
                  <select value={method} onChange={(ev) => setMethod(ev.target.value)} data-testid="settlement-reconcile-method" className="mt-0.5 block h-8 w-48 rounded border border-[#C2C9D1] px-1.5 text-xs">
                    <option value="">— Select —</option>
                    {methodOpts.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
              )}
              {/* ★ REFUSED, NOT WARNED ABOUT — AND THE SERVER REFUSES TOO.
                  (Adversarial review, round 8; the server half is ZEN-637.)
                  The note above said "enter an exchange rate first" and then
                  nothing stopped this button — so a family could mark an
                  unpriced expense reimbursed while the amount RECORDED against
                  it excluded that expense entirely. Real money, filed wrong,
                  with a warning that had no teeth.
                  A disclosure nothing acts on is the fault this whole change
                  exists to remove, and here it had the power to mis-record a
                  payment. The action is refused while the selection contains
                  something nobody could price — the same answer the split door
                  already gives: refuse, and say why. */}
              <button onClick={reconcile} disabled={busy || !selected.length || selUnpriced > 0} data-testid="settlement-reconcile-btn"
                className="ml-auto inline-flex h-8 items-center gap-1 rounded-md bg-[#077A39] px-3 text-xs font-bold text-white hover:bg-[#066330] disabled:opacity-50">
                <CheckCircle size={14} weight="bold" /> {busy ? "Saving…" : `Mark reimbursed${recordsInNativeCurrency && selNativeTotal !== null ? ` (${money(selNativeTotal, nativeCur)})` : ""}`}
              </button>
            </div>
            <div className="mt-1.5">
              <NotesField value={note} onChange={setNote} testid="settlement-reconcile-note" />
            </div>
          </>}
    </div>
  );
}

function SettlementEditDialog({ open, settlement, onClose, onSaved, area = "private" }) {
  const [f, setF] = useState({ date: "", method: "", note: "", from_account: "", to_account: "" });
  const [busy, setBusy] = useState(false);
  const { data: banks = [] } = useQuery({ queryKey: ["accounts-cards"], queryFn: async () => (await api.get("/accounts-cards")).data, enabled: open && area !== "private" });
  const { fromOpts, toOpts } = useSettlementAccounts(settlement?.student_id, settlement?.payor_ref, settlement?.reimburser_ref,
    { from: settlement?.from_account, to: settlement?.to_account });

  React.useEffect(() => {
    if (open && settlement) {
      setF({
        date: settlement.date || "",
        method: settlement.method || settlement.reimbursement_method || "",
        note: settlement.note || settlement.notes || "",
        from_account: settlement.from_account || "",
        to_account: settlement.to_account || "",
      });
    }
  }, [open, settlement]);

  const save = async () => {
    if (!settlement) return;
    setBusy(true);
    try {
      const patch = {};
      if (f.date && f.date !== settlement.date) patch.date = f.date;
      const origMethod = settlement.method || settlement.reimbursement_method || "";
      if (f.method !== origMethod) patch.method = f.method;
      const origNote = settlement.note || settlement.notes || "";
      if (f.note !== origNote) patch.note = f.note;
      if (f.from_account !== (settlement.from_account || "")) patch.from_account = f.from_account;
      if (f.to_account !== (settlement.to_account || "")) patch.to_account = f.to_account;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.patch(`/settlements/${settlement.id}`, patch);
      toast.success("Settlement updated");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit settlement metadata</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Date
            <UnifiedDateInput value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="edit-settlement-date" />
          </label>
          {area !== "private" && (
            <label className="block text-xs font-bold uppercase tracking-wide text-[#6b7280]">Method
              <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="edit-settlement-method" className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm">
                <option value="">— None —</option>
                {METHODS_529_EDIT.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          )}
          <NotesField value={f.note} onChange={(v) => setF({ ...f, note: v })} testid="edit-settlement-note" />
          <div className="grid grid-cols-2 gap-3">
            {area === "private" ? (
              <SettlementAccountBoxes fromOpts={fromOpts} toOpts={toOpts} from={f.from_account} to={f.to_account}
                onFrom={(v) => setF({ ...f, from_account: v })} onTo={(v) => setF({ ...f, to_account: v })} testid="edit-settlement"
                labelClass="block text-xs font-bold uppercase tracking-wide text-[#6b7280]"
                selectClass="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" />
            ) : (<>
            <label className="block text-xs font-bold uppercase tracking-wide text-[#6b7280]">From account
              <select value={f.from_account} onChange={(e) => setF({ ...f, from_account: e.target.value })} data-testid="edit-settlement-from" className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm">
                <option value="">—</option>
                {banks.map((b) => <option key={b.id} value={b.label}>{b.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-bold uppercase tracking-wide text-[#6b7280]">To account
              <select value={f.to_account} onChange={(e) => setF({ ...f, to_account: e.target.value })} data-testid="edit-settlement-to" className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm">
                <option value="">—</option>
                {banks.map((b) => <option key={b.id} value={b.label}>{b.label}</option>)}
              </select>
            </label>
            </>)}
          </div>
          <p className="text-[10px] text-[#9aa1a9]">Amount, area, and party refs cannot be edited. To correct those, undo the settlement and re-reconcile.</p>
        </div>
        <DialogFooter>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn enterPrimary variant="green" onClick={save} disabled={busy} data-testid="edit-settlement-save">{busy ? "Saving…" : "Save"}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What a repayment was, in the currency it was paid in — always named on a
 *  private settlement. A 529 settlement keeps the wording it had. */
const paidText = (s, area = "private") => {
  const usd = s.amount_usd ?? s.amount;
  const foreign = curCode(s.currency) !== "USD";
  if (area !== "private") return foreign ? `${money(s.amount, s.currency)} (${money(usd, "USD")})` : money(usd, "USD");
  return foreign ? `${money(s.amount, s.currency)} (${money(usd, "USD")})` : money(usd, "USD");
};
const expenseCount = (s) => { const n = (s.linked_expense_ids || []).length; return n ? ` · ${n} expense${n === 1 ? "" : "s"}` : ""; };

// What one payment covered, opened by the same "View details" link as a 529
// distribution on the Withdrawals screen. (ZEN-755)
function PaymentExpenses({ settlementId }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["settlement-detail", settlementId],
    queryFn: async () => (await api.get(`/settlements/${settlementId}/detail`)).data,
    enabled: !!settlementId,
  });
  const linked = data?.linked_expenses || [];
  return (
    <div className="mt-1.5 rounded-md bg-white p-2.5" data-testid={`settlement-history-details-${settlementId}`}>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Expenses this payment covered</div>
      {isLoading ? <div className="text-xs text-[#6b7280]">Loading…</div>
        // Not being able to ask is not the same as an answer of "none".
        : (isError || !data) ? <div className="text-xs text-[#92600A]" data-testid={`settlement-history-details-error-${settlementId}`}>Couldn&apos;t load the expenses for this payment. Close and open it again to retry.</div>
        : linked.length === 0 ? <div className="text-xs text-[#6b7280]">This older repayment didn&apos;t say which expenses it paid back.</div>
          : <div className="divide-y divide-[#E9E9EB]">
            {linked.map((e) => {
              const cur = curCode(e.original_currency);
              return (
                <div key={e.id} className="flex items-start gap-2 py-1 text-xs" data-testid={`settlement-history-exp-${e.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{e.payee || "—"}</div>
                    <div className="text-[#6b7280]">{fmtDate(e.transaction_date)}{e.category ? ` · ${e.category}` : ""}</div>
                  </div>
                  <div className="shrink-0 whitespace-nowrap text-right tnum">
                    {cur !== "USD" && <div className="text-[#6b7280]">{money(e.original_amount, cur)}</div>}
                    <div className="font-bold">{money(e.usd_amount, "USD")}</div>
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

// Finished private settlements, newest year first. A payment with no date has
// no year to sit under, so it gets a heading of its own. (ZEN-755)
const NO_DATE = "No date";
const byYear = (rows) => {
  const groups = [];
  for (const s of rows) {
    const year = /^\d{4}/.test(s.date || "") ? s.date.slice(0, 4) : NO_DATE;
    const g = groups.find((x) => x.year === year) || groups[groups.push({ year, rows: [] }) - 1];
    g.rows.push(s);
  }
  return groups;
};
// A private payment says the accounts it moved between; an older one, or a 529
// one, keeps the method it was saved with. (ZEN-799)
const historyAccounts = (s, area) => (area === "private" && accountsPhrase(s.from_account, s.to_account))
  || s.method || s.reimbursement_method || "";

const yearTotal = (rows) => rows.reduce((t, s) => t + (Number(s.amount_usd ?? s.amount) || 0), 0);

function SettlementHistory({ sid, c, area = "private" }) {
  const { data: all = [], isLoading } = useQuery({ queryKey: ["settlements", sid], queryFn: async () => (await api.get(`/settlements?student_id=${sid}`)).data, enabled: !!sid });
  const rows = all
    .filter((s) => (s.area || "private") === area && s.payor_ref === c.payor_ref && s.reimburser_ref === c.reimburser_ref)
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const [undoConfirm, setUndoConfirm] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  // The settlement behind the eye — read only. (ZEN-702)
  const [viewTarget, setViewTarget] = useState(null);
  // Which payments show their expenses, and which years the owner has opened
  // or closed by hand. This year starts open, earlier years closed. (ZEN-755)
  const [details, setDetails] = useState({});
  const [flipped, setFlipped] = useState([]);
  const thisYear = String(new Date().getFullYear());
  const isYearOpen = (y) => (y === thisYear) !== flipped.includes(y);
  const flipYear = (y) => setFlipped((f) => (f.includes(y) ? f.filter((x) => x !== y) : [...f, y]));

  const undoSettlement = async (id) => {
    try {
      await api.delete(`/settlements/${id}`);
      toast.success("Settlement undone — linked expenses reverted to Awaiting.");
      setUndoConfirm(null);
    } catch { toast.error("Could not undo"); }
  };
  // Nothing to refresh by hand: the save itself re-checks every figure. (ZEN-735)
  const onEditSaved = () => {};

  const row = (s) => (
    <div key={s.id} className="py-1.5 text-xs" data-testid={`settlement-history-row-${s.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 flex-1">{fmtDate(s.date)} · <PaidAmount testid={`settlement-history-amount-${s.id}`}>{paidText(s, area)}</PaidAmount>{area === "private" ? expenseCount(s) : ""}{historyAccounts(s, area) ? <> · <span>{historyAccounts(s, area)}</span></> : ""}</span>
        <div className="flex items-center gap-1">
          {/* The eye, immediately before the pencil. (ZEN-702) */}
          <ViewButton variant="bare" className="rounded p-1 hover:bg-[#E9E9EB]" onClick={() => setViewTarget(s)} testid={`settlement-history-view-${s.id}`} size={13} />
          <button onClick={() => setEditTarget(s)} data-testid={`settlement-history-edit-${s.id}`} className="rounded p-1 text-[#034AA2] hover:bg-[#E9E9EB]" aria-label="edit"><PencilSimple size={13} weight="bold" /></button>
          <button onClick={() => setUndoConfirm(s)} data-testid={`settlement-history-undo-${s.id}`} className="rounded p-1 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#D97706]" aria-label="undo"><ArrowCounterClockwise size={13} weight="bold" /></button>
        </div>
      </div>
      {area === "private" && (
        <>
          <button onClick={() => setDetails((d) => ({ ...d, [s.id]: !d[s.id] }))} data-testid={`settlement-history-details-btn-${s.id}`} className="mt-0.5 text-xs font-semibold text-[#034AA2] hover:underline">
            {details[s.id] ? "Hide details" : "View details"}
          </button>
          {details[s.id] && <PaymentExpenses settlementId={s.id} />}
        </>
      )}
    </div>
  );

  return (
    <div className="mt-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2.5" data-testid="settlement-history">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Settlement history · {c.reimburser_nick} → {c.payor_nick}</div>
      {isLoading ? <div className="text-xs text-[#6b7280]">Loading…</div>
        : rows.length === 0 ? <div className="text-xs text-[#6b7280]">No reimbursements recorded yet.</div>
          : <div className="divide-y divide-[#E9E9EB]">
            {area === "private"
              ? byYear(rows).map((g) => (
                <div key={g.year} data-testid={`settlement-history-year-${g.year}`}>
                  <button onClick={() => flipYear(g.year)} aria-expanded={isYearOpen(g.year)}
                    data-testid={`settlement-history-year-btn-${g.year}`} className="flex w-full items-center gap-1.5 py-1.5 text-left text-xs">
                    <CaretDown size={12} weight="bold" className={`shrink-0 text-[#6b7280] transition-transform ${isYearOpen(g.year) ? "" : "-rotate-90"}`} />
                    <b>{g.year}</b>
                    <span className="text-[#6b7280]">· {g.rows.length} payment{g.rows.length === 1 ? "" : "s"} · {money(yearTotal(g.rows), "USD")}</span>
                  </button>
                  {isYearOpen(g.year) && <div className="divide-y divide-[#E9E9EB] pl-4">{g.rows.map(row)}</div>}
                </div>
              ))
              : rows.map(row)}
          </div>}
      {/* Undo confirmation dialog */}
      <DeleteConfirmDialog open={!!undoConfirm} onClose={() => setUndoConfirm(null)} onConfirm={() => undoSettlement(undoConfirm?.id)}
        title="Undo this settlement?"
        description={<>
          {undoConfirm && (
            <div className="mb-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-3 py-2 text-sm">
              <div className="font-semibold text-[#D97706]">{paidText(undoConfirm, area)}</div>
              <div className="text-xs text-[#6b7280]">{fmtDate(undoConfirm.date)}{(undoConfirm.method || undoConfirm.reimbursement_method) ? ` · ${undoConfirm.method || undoConfirm.reimbursement_method}` : ""}</div>
            </div>
          )}
          Any expenses linked to this payment will revert to <strong>Awaiting</strong> and need to be re-settled.
        </>} />
      {/* Edit dialog */}
      <SettlementEditDialog open={!!editTarget} settlement={editTarget} onClose={() => setEditTarget(null)} onSaved={onEditSaved} area={area} />
      <RecordView
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        title={RECORD_TITLES.settlement}
        fields={viewTarget ? settlementFields(viewTarget, { between: `${c.reimburser_nick} → ${c.payor_nick}` }) : []}
        testid="settlement-view"
      />
    </div>
  );
}

function DeferredRollup({ items, studentId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [histIdx, setHistIdx] = useState(null);
  const owed = (items || []).filter((c) => c.net > 0.005);
  if (!owed.length) return null;
  const total = owed.reduce((s, c) => s + c.net, 0);

  return (
    <Card data-testid="deferred-rollup" className="border-[#D97706]/40">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3" data-testid="deferred-rollup-toggle">
        <div className="flex items-center gap-2.5">
          <ClockCountdown size={20} weight="bold" className="shrink-0 text-[#D97706]" />
          <div className="text-left">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#1f2937]">Private settlements awaiting</h3>
            <p className="text-xs text-[#6b7280]">Owed but not yet paid back · {owed.length} item{owed.length === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="tnum text-xl font-extrabold text-[#D97706]">{money(total, "USD")}</span>
          <CaretDown size={16} weight="bold" className={`text-[#6b7280] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="mt-3 divide-y divide-[#C2C9D1] border-t border-[#C2C9D1] pt-1">
          {owed.map((c, n) => { const i = cardKey(c); return (
            <div key={i} className="py-2" data-testid={`deferred-item-${c.reimburser_nick}-${c.payor_nick}`}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <div className="font-semibold">{c.student ? <span className="text-[#034AA2]">{c.student} · </span> : null}{c.reimburser_nick} <ArrowRight size={12} weight="bold" className="inline -mt-0.5 text-[#6b7280]" /> {c.payor_nick}</div>
                  {!c.has_older_repayment && c.awaiting_count > 0 && <div className="text-xs text-[#6b7280]">{c.awaiting_count} expense{c.awaiting_count === 1 ? "" : "s"}</div>}
                  <CardBreakdown c={c} className="text-xs text-[#6b7280]" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="tnum font-bold text-[#D97706]">{owedText(c)}</div>
                  <div className="flex items-center gap-2">
                    {c.settled > 0.005 && (
                      <button onClick={() => doUndoLastSettlement(qc, c.student_id || studentId, c)} data-testid={`deferred-undo-btn-${n}`}
                        className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#6b7280] hover:text-[#D97706] hover:underline">
                        <ArrowCounterClockwise size={14} weight="bold" /> Undo last
                      </button>
                    )}
                    <button onClick={() => { if (histIdx !== i) recheckEverythingOnScreen(qc); setHistIdx(histIdx === i ? null : i); }} data-testid={`deferred-history-btn-${n}`}
                      className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#034AA2] hover:underline">
                      <ClockCounterClockwise size={14} weight="bold" /> History
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-1 text-[11px] text-[#6b7280]">Use the settlement panel below to record reimbursements for individual expenses.</div>
              {histIdx === i && <SettlementHistory sid={c.student_id || studentId} c={c} />}
            </div>
          ); })}
        </div>
      )}
    </Card>
  );
}


export { MoneyArea, doUndoLastSettlement, DeferredRollup };
